import Script from 'next/script';
import { useEffect } from 'react';

import { APPLOVIN_PIXEL_ID } from '@features/Analytics/constants';

const APPLOVIN_SOURCE_COOKIE_NAME = '_axwrt';
const APPLOVIN_TARGET_COOKIE_NAME = 'axwrt';
const APPLOVIN_COOKIE_SYNC_ATTEMPTS = 20;
const APPLOVIN_COOKIE_SYNC_INTERVAL_MS = 500;
const APPLOVIN_COOKIE_TTL_DAYS = 365;

const getCookieValue = (name: string) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  const value = cookie.slice(name.length + 1);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getAppLovinCookieDomain = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = window.location.hostname.replace(/^www\./, '');
  const isIpAddress = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname);

  if (
    hostname === 'localhost' ||
    hostname.includes(':') ||
    isIpAddress ||
    !hostname.includes('.')
  ) {
    return '';
  }

  return `; domain=.${hostname}`;
};

const syncAppLovinCookie = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  const axonCookie = getCookieValue(APPLOVIN_SOURCE_COOKIE_NAME);

  if (!axonCookie) {
    return false;
  }

  if (getCookieValue(APPLOVIN_TARGET_COOKIE_NAME) === axonCookie) {
    return true;
  }

  const expiresAt = new Date(
    Date.now() + APPLOVIN_COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString();

  document.cookie = `${APPLOVIN_TARGET_COOKIE_NAME}=${encodeURIComponent(
    axonCookie,
  )}; expires=${expiresAt}; path=/; SameSite=Lax${getAppLovinCookieDomain()}`;

  return getCookieValue(APPLOVIN_TARGET_COOKIE_NAME) === axonCookie;
};

export const AppLovinPixel = () => {
  useEffect(() => {
    if (!APPLOVIN_PIXEL_ID || syncAppLovinCookie()) {
      return;
    }

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;

      if (syncAppLovinCookie() || attempts >= APPLOVIN_COOKIE_SYNC_ATTEMPTS) {
        window.clearInterval(intervalId);
      }
    }, APPLOVIN_COOKIE_SYNC_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  return APPLOVIN_PIXEL_ID ? (
    <Script id="applovin-pixel">
      {`var AXON_EVENT_KEY=${JSON.stringify(APPLOVIN_PIXEL_ID)};
!function(e,r){var t=["https://s.axon.ai/pixel.js","https://res4.applovin.com/p/l/loader.iife.js"];if(!e.axon){var a=e.axon=function(){a.performOperation?a.performOperation.apply(a,arguments):a.operationQueue.push(arguments)};a.operationQueue=[],a.ts=Date.now(),a.eventKey=AXON_EVENT_KEY;for(var n=r.getElementsByTagName("script")[0],o=0;o<t.length;o++){var i=r.createElement("script");i.async=!0,i.src=t[o],n.parentNode.insertBefore(i,n)}}}(window,document);
axon("init");
axon("track", "page_view");`}
    </Script>
  ) : null;
};
